/**
 * Single Row Template Parameters
 *
 * Defines form parameters and Zod schema for single-row hangars
 * (multiple box units in a row)
 */

import { z } from 'zod'
import type { BaseTemplateParams, DoorSpec, DoorType, UnitOverrideConfig, UnitDoorsConfig } from '../types'
import { DOOR_TYPE_DEFAULTS } from '../types'

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

/** Door specification schema */
export const doorSpecSchema = z.object({
  offset_ft: z.number().min(0, 'Offset must be non-negative'),
  width_ft: z.number().min(10, 'Door width must be at least 10 ft'),
  height_ft: z.number().min(8, 'Door height must be at least 8 ft'),
  type: z.enum(['bifold', 'sliding', 'hydraulic', 'rolling']),
})

/** Per-wall door configuration schema */
export const unitDoorsConfigSchema = z.object({
  front: z.array(doorSpecSchema).optional(),
  back: z.array(doorSpecSchema).optional(),
  left: z.array(doorSpecSchema).optional(),
  right: z.array(doorSpecSchema).optional(),
})

/** Unit override schema */
export const unitOverrideSchema = z.object({
  index: z.number().int().min(0),
  type: z.enum([
    'conventional_hangar', 'box_hangar', 't_hangar', 'shade',
    'tiedown', 'ramp', 'office', 'storage', 'shop', 'custom',
  ]).optional(),
  width_ft: z.number().min(15, 'Unit width must be at least 15 ft').optional(),
  doors: unitDoorsConfigSchema.optional(),
})

/** Single row parameters schema */
export const singleRowParamsSchema = z.object({
  // Base params
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required').max(10, 'Code too long'),
  latitude: z.number(),
  longitude: z.number(),
  bearing_deg: z.number().transform(val => ((val % 360) + 360) % 360),
  floor_color: z.string(),
  wall_color: z.string(),

  // Envelope dimensions
  width_ft: z.number().min(30, 'Width must be at least 30 ft').max(1000),
  depth_ft: z.number().min(20, 'Depth must be at least 20 ft').max(500),
  height_ft: z.number().min(12, 'Height must be at least 12 ft').max(100),

  // Unit configuration
  unit_count: z.number().int().min(1, 'At least 1 unit required').max(20, 'Max 20 units'),

  // Default door configuration (applied to all units unless overridden)
  default_door_type: z.enum(['bifold', 'sliding', 'hydraulic', 'rolling']),
  default_door_width_ft: z.number().min(10).max(200),
  default_door_height_ft: z.number().min(8).max(50),

  // Per-unit overrides
  unit_overrides: z.array(unitOverrideSchema).default([]),

  // Spacing
  wall_thickness_ft: z.number().min(0.25).max(2).default(0.5),
})

// ============================================================================
// TYPES
// ============================================================================

/** Single row template parameters interface */
export interface SingleRowParams extends BaseTemplateParams {
  // Envelope dimensions
  width_ft: number
  depth_ft: number
  height_ft: number

  // Unit configuration
  unit_count: number

  // Default door configuration
  default_door_type: DoorType
  default_door_width_ft: number
  default_door_height_ft: number

  // Per-unit overrides
  unit_overrides: UnitOverrideConfig[]

  // Spacing
  wall_thickness_ft: number
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/** Default values for single row template */
export const DEFAULT_SINGLE_ROW_VALUES: SingleRowParams = {
  name: 'New Box Hangar Row',
  code: 'BH',
  latitude: 0,
  longitude: 0,
  bearing_deg: 0,
  floor_color: '#808080',
  wall_color: '#A0A0A0',

  width_ft: 200,
  depth_ft: 50,
  height_ft: 24,

  unit_count: 5,

  default_door_type: 'bifold',
  default_door_width_ft: 36,
  default_door_height_ft: 20,

  unit_overrides: [],

  wall_thickness_ft: 0.5,
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Create default door specification from params
 */
export function createDefaultDoorSpec(params: SingleRowParams, unitWidth: number): DoorSpec {
  // Center door in unit
  const doorWidth = Math.min(params.default_door_width_ft, unitWidth - 4) // Leave 2ft on each side

  return {
    // offset_ft is the leading edge (left side) of the door, not center
    // To center the door: (unitWidth - doorWidth) / 2
    offset_ft: (unitWidth - doorWidth) / 2,
    width_ft: doorWidth,
    height_ft: params.default_door_height_ft,
    type: params.default_door_type,
  }
}

/**
 * Get door specifications for a unit (respecting overrides)
 * Returns per-wall door configuration
 */
export function getUnitDoors(
  params: SingleRowParams,
  unitIndex: number,
  unitWidth: number,
  unitCount: number
): UnitDoorsConfig {
  const isFirstUnit = unitIndex === 0
  const isLastUnit = unitIndex === unitCount - 1

  // Check for override
  const override = params.unit_overrides.find(o => o.index === unitIndex)

  if (override?.doors) {
    // Use override doors, but validate side doors for end units only
    const result: UnitDoorsConfig = {
      front: override.doors.front,
      back: override.doors.back,
    }
    // Only allow left door for first unit
    if (isFirstUnit && override.doors.left) {
      result.left = override.doors.left
    }
    // Only allow right door for last unit
    if (isLastUnit && override.doors.right) {
      result.right = override.doors.right
    }
    // If no front doors specified, use default
    if (!result.front || result.front.length === 0) {
      result.front = [createDefaultDoorSpec(params, unitWidth)]
    }
    return result
  }

  // Use default door on front wall
  return {
    front: [createDefaultDoorSpec(params, unitWidth)],
  }
}

/**
 * Legacy helper - get front wall doors only (for backwards compatibility)
 */
export function getUnitFrontDoors(
  params: SingleRowParams,
  unitIndex: number,
  unitWidth: number,
  unitCount: number
): DoorSpec[] {
  return getUnitDoors(params, unitIndex, unitWidth, unitCount).front || []
}

/**
 * Get door width constraints for door type
 */
export function getDoorConstraints(doorType: DoorType): { min: number; max: number } {
  const defaults = DOOR_TYPE_DEFAULTS[doorType]
  return {
    min: defaults.min_width_ft,
    max: defaults.max_width_ft,
  }
}

/**
 * Validate door width is within type constraints
 */
export function isValidDoorWidth(doorType: DoorType, width: number): boolean {
  const { min, max } = getDoorConstraints(doorType)
  return width >= min && width <= max
}
